import { TableDataProps } from "@/pages/ContestInvestigationSummary";
import { ApiDataProps } from "@/pages/Tenants";
import { TypePageActions } from "@/pages/Plans";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ComponentControlsPagination } from "@/components/Pagination";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mask } from "@/utils/mask";

type TableProps = {
  dataApi: ApiDataProps<TableDataProps>;
  pageActions: TypePageActions;
  updateFront: (data: ApiDataProps<TableDataProps>) => void;
};

export const TableData = ({ dataApi, pageActions }: TableProps) => {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [clientSearches, setClientSearches] = useState<Record<string, string>>(
    {}
  );

  const toggleRow = (rowId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const handleSearchChange = (rowId: string, value: string) => {
    setClientSearches((prev) => ({
      ...prev,
      [rowId]: value.toLowerCase(),
    }));
  };

  const filterClients = (
    clients: TableDataProps["clients"],
    searchTerm: string
  ) => {
    if (!searchTerm) return clients;
    return clients.filter(
      (client) =>
        client?.client_name?.toLowerCase().includes(searchTerm)
    );
  };

  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Concurso</TableHead>
            <TableHead>Data de Validação</TableHead>
            <TableHead>Loteria</TableHead>
            <TableHead>Total de Apurações</TableHead>
            <TableHead>Valor Total por Cota</TableHead>
            <TableHead>Clientes</TableHead>
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((item) => {
            const rowId = `${item.contest_id}-${item.lottery_name}`;
            const isExpanded = expandedRows[rowId];
            const searchTerm = clientSearches[rowId] || "";
            const filteredClients = filterClients(item?.clients, searchTerm);

            return (
              <>
                <TableRow key={rowId}>
                  <TableCell>{item.contest_name}</TableCell>
                  <TableCell>
                    {Mask.transformMaskBirthdayInBr(item.contest_validation_date)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: item.lottery_color }}
                      />
                      {item.lottery_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    {Mask.formatCurrency(item?.total_investigation || 0)}
                  </TableCell>
                  <TableCell>
                    {Mask.formatCurrency(item.total_price_per_share)}
                  </TableCell>
                  <TableCell>
                    {item.clients.length} cliente(s)
                  </TableCell>
                  <TableCell>
                    {item.clients.length > 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleRow(rowId)}
                        className="text-primary hover:bg-zinc-100 dark:hover:bg-zinc-700"
                      >
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
                {isExpanded && item.clients.length > 0 && (
                  <TableRow key={`details-${rowId}`}>
                    <TableCell colSpan={7} className="p-0">
                      <div className="p-4 bg-zinc-50 dark:bg-zinc-800">
                        <div className="mb-4 flex items-center gap-2">
                          <Search className="w-4 h-4 text-gray-500" />
                          <Input
                            type="text"
                            placeholder="Buscar por nome do cliente..."
                            value={searchTerm}
                            onChange={(e) =>
                              handleSearchChange(rowId, e.target.value)
                            }
                            className="max-w-sm"
                          />
                        </div>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Cliente</TableHead>
                              <TableHead>Documento</TableHead>
                              <TableHead>Telefone</TableHead>
                              <TableHead>Total Investido</TableHead>
                              <TableHead>Premiado</TableHead>
                              <TableHead>Valor do Prêmio</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredClients
                              ? filteredClients.map((client, index) => (
                                  <TableRow
                                    key={`${client.client_id}-${index}`}
                                  >
                                    <TableCell className="font-medium">
                                      {client?.client_name}
                                    </TableCell>
                                    <TableCell>
                                      {Mask.maskCpfOrCnpj(client?.client_document)}
                                    </TableCell>
                                    <TableCell>
                                      {Mask.maskPhone( client?.client_phone)}
                                    </TableCell>
                                    <TableCell>
                                      {Mask.formatCurrency(
                                        Number(client?.total_invested)
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {client.is_winner ? "Sim" : "Não"}
                                    </TableCell>
                                    <TableCell>
                                      {Mask.formatCurrency(
                                        Number(client?.prize_amount)
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))
                              : "Nenhum cliente encontrado"}
                          </TableBody>
                        </Table>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
          })}
        </TableBody>
      </Table>
      <ComponentControlsPagination
        dataApi={dataApi}
        pageActions={pageActions}
      />
    </>
  );
};
